"use client";

import useSWR from "swr";
import { useMemo } from "react";
import LocationBasedItems from "@/components/ui/LocationBasedItems";
import { useLocation } from "@/contexts/LocationContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { getRecentlyConfirmedItems } from "@/lib/itemsAPI";
import {
  prepareRecentlyConfirmedShelfItems,
  RECENTLY_CONFIRMED_QUERY_POOL_LIMIT,
  RECENTLY_CONFIRMED_SHELF_MAX_ITEMS,
  RECENTLY_CONFIRMED_SHELF_SUBTITLE,
  RECENTLY_CONFIRMED_SHELF_TITLE,
} from "@/lib/items/recentlyConfirmedNearYou";
import type { Item } from "@/lib/types";

interface RecentlyConfirmedShelfProps {
  onItemClick?: (item: Item) => void;
  viewAllLink?: string;
}

export default function RecentlyConfirmedShelf({
  onItemClick,
  viewAllLink,
}: RecentlyConfirmedShelfProps) {
  const { userLocation, isLoadingLocation } = useLocation();
  const {
    buyerAvailabilityConfirmedShelfEnabled,
    buyerAvailabilityConfirmedWindowDays,
    includeOtherLocationTier,
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
    isLoading: settingsLoading,
  } = useSystemSettings();

  const shouldFetch =
    !settingsLoading &&
    buyerAvailabilityConfirmedShelfEnabled &&
    !isLoadingLocation &&
    Boolean(userLocation?.areaName?.trim());

  const { data: pool = [], isLoading: itemsLoading } = useSWR<Item[]>(
    shouldFetch
      ? [
          "recently-confirmed-shelf",
          buyerAvailabilityConfirmedWindowDays,
          RECENTLY_CONFIRMED_QUERY_POOL_LIMIT,
        ]
      : null,
    () =>
      getRecentlyConfirmedItems(
        RECENTLY_CONFIRMED_QUERY_POOL_LIMIT,
        buyerAvailabilityConfirmedWindowDays,
      ),
    {
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000,
    },
  );

  const shelfItems = useMemo(
    () =>
      prepareRecentlyConfirmedShelfItems(pool, {
        userLocation: userLocation
          ? {
              areaName: userLocation.areaName ?? "",
              formattedAddress: userLocation.formattedAddress,
            }
          : null,
        includeOtherLocationTier,
        windowDays: buyerAvailabilityConfirmedWindowDays,
        enabled: buyerAvailabilityConfirmedShelfEnabled,
        discoveryGranularity: homepageDiscoveryGranularity,
        discoveryAutoWiden: homepageDiscoveryAutoWidenEnabled,
      }),
    [
      pool,
      userLocation,
      includeOtherLocationTier,
      buyerAvailabilityConfirmedWindowDays,
      buyerAvailabilityConfirmedShelfEnabled,
      homepageDiscoveryGranularity,
      homepageDiscoveryAutoWidenEnabled,
    ],
  );

  if (
    settingsLoading ||
    !buyerAvailabilityConfirmedShelfEnabled ||
    isLoadingLocation ||
    !userLocation?.areaName?.trim()
  ) {
    return null;
  }

  if (itemsLoading && shelfItems.length === 0) {
    return null;
  }

  if (shelfItems.length === 0) {
    return null;
  }

  return (
    <LocationBasedItems
      items={shelfItems}
      title={RECENTLY_CONFIRMED_SHELF_TITLE}
      subtitle={RECENTLY_CONFIRMED_SHELF_SUBTITLE}
      viewAllLink={viewAllLink}
      onItemClick={onItemClick}
      maxItemsForDistance={RECENTLY_CONFIRMED_SHELF_MAX_ITEMS}
    />
  );
}
